import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Inject,
  input,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import {
  AddressModelKeys,
  Agent,
  AGENT_SEARCH_KEYS_CONFIG,
  agentChangeLogsLookup,
  agentEmailAddressesKeySet,
  AgentKeys,
  AssociationKeys,
  ASSOCIATIONS_SEARCH_KEYS_CONFIG,
  CHANGE_SOURCE_TYPE_LOOKUP,
  ChangeLogsHistoryType,
  changeLogsLookup,
  ChangeLogsType,
  ChangeSourceType,
  Constants,
  EMAIL_ADDRESS_SEARCH_KEYS_CONFIG,
  EmailAddressKeys,
  excludedChangeLogsFieldsSet,
  GuestKeys,
  GUESTS_SEARCH_KEYS_CONFIG,
  LookupKeys,
  PERSONAL_INFO_SEARCH_KEYS_CONFIG,
  POLICY_SIGNERS_SEARCH_KEYS_CONFIG,
  RegistrantKeys,
  RSVP_SEARCH_KEYS_CONFIG,
} from '@ag-common-lib/public-api';
import { DxDataGridComponent } from 'devextreme-angular';
import { ChangesHistoryGridService } from './changes-history-grid.service';
import { DOCUMENT } from '@angular/common';
import { DocumentWriteLogElasticSearchService } from 'ag-common-svc/lib/services/elastic-search.services/documet-write-log-elastic-search.service';
import { fullscreen, setPopupsContainer } from 'ag-common-svc/shared/utils/full-screen';
import { BehaviorSubject, EMPTY, firstValueFrom } from 'rxjs';
import { getChangedLog, isLogArray, isRemovedLog } from './utils/normalize-log';
import { Attendee } from 'ag-common-svc/lib/utils/attendees';
import ArrayStore from 'devextreme/data/array_store';
import { sortLookupByDescription } from 'ag-common-svc/shared/utils/sort-lookup-by-description';
import { WasabiFile } from 'ag-common-svc/shared/pipes/wasabi-file.pipe';
import { getJsonFromWasabiFile } from 'ag-common-svc/lib/utils/json-from-wasabifile.util';
import { toSignal } from '@angular/core/rxjs-interop';
import { removeDiacritics } from 'ag-common-svc/shared/utils/remove-diacritics';
import { DOCUMENT_WRITE_ACTION_LOOKUP, DocumentWriteAction } from '@ag-common-lib/public-api';
import { LoggedCollections } from '../../../../../../../ag-functions/src/log-document-writes/log-document-writes.model';

@Component({
  selector: 'ag-shr-changes-history-grid',
  templateUrl: './changes-history-grid.component.html',
  styleUrls: ['./changes-history-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WasabiFile],
})
export class ChangesHistoryGridComponent implements OnInit {
  @HostBinding('class') className = 'ag-changes-history';
  @ViewChild('changeLogGridRef', { static: false }) changeLogGridComponent!: DxDataGridComponent;
  @ViewChild('changeLogContainerRef', { static: true }) changeLogContainer: ElementRef;

  canEditSig = input<boolean>(true, { alias: 'canEdit' });
  canDeleteSig = input<boolean>(true, { alias: 'canDelete' });
  canCreateSig = input<boolean>(true, { alias: 'canCreate' });
  changeLogTypeSig = input<ChangeLogsHistoryType>(ChangeLogsHistoryType.agent, { alias: 'changeLogType' });

  protected documentWriteLogElasticSearchService = inject(DocumentWriteLogElasticSearchService);
  private changesHistoryService = inject(ChangesHistoryGridService);
  private wasabiFilePipe = inject(WasabiFile);

  @Input()
  set agent(value: Agent) {
    this.changesHistoryService.setAgentData(value);
  }

  @Input()
  set attendee(value: Attendee) {
    this.changesHistoryService.setAttendeeData(value);
  }

  @HostListener('document:fullscreenchange') fullScreenMode() {
    if (!document.fullscreenElement) {
      document.body.classList.remove('fullscreen-enable');
      setPopupsContainer();
    }
  }

  agentsLookupSig = toSignal(this.changesHistoryService.agentsLookupBase$);
  registrantGuestsLookupSig = toSignal(this.changesHistoryService.registrantGuestsLookup$, { initialValue: [] });

  containerElement: HTMLElement;
  conferenceDbId: string;
  registrantDbId: string;
  agentDbId: string;
  collectionFilterValues = [];
  wasabiPathFilterValues = [];

  readonly filterPanelText$ = new BehaviorSubject('');
  protected dataSource: CustomStore;
  protected updatedFieldsList: DataSource;
  protected readonly dateFormat: string = Constants.DISPLAY_DATE_TIME_FORMAT;
  protected readonly ChangeLogsHistoryType = ChangeLogsHistoryType;
  protected readonly PERSONAL_INFO_SEARCH_KEYS_CONFIG = PERSONAL_INFO_SEARCH_KEYS_CONFIG;
  protected readonly GUESTS_SEARCH_KEYS_CONFIG = GUESTS_SEARCH_KEYS_CONFIG;
  protected readonly RSVP_SEARCH_KEYS_CONFIG = RSVP_SEARCH_KEYS_CONFIG;
  protected readonly AGENT_SEARCH_KEYS_CONFIG = AGENT_SEARCH_KEYS_CONFIG;
  protected readonly changeSourceTypeLookup = sortLookupByDescription(CHANGE_SOURCE_TYPE_LOOKUP);
  protected readonly GuestKeys = GuestKeys;
  protected readonly AgentKeys = AgentKeys;
  protected readonly RegistrantKeys = RegistrantKeys;
  protected readonly getChangedLog = getChangedLog;
  protected readonly excludedChangeLogsFieldsSet = excludedChangeLogsFieldsSet;
  protected readonly LoggedCollections = LoggedCollections;
  protected readonly LookupKeys = LookupKeys;
  protected readonly EmailAddressKeys = EmailAddressKeys;
  protected readonly EMAIL_ADDRESS_SEARCH_KEYS_CONFIG = EMAIL_ADDRESS_SEARCH_KEYS_CONFIG;
  protected readonly POLICY_SIGNERS_SEARCH_KEYS_CONFIG = POLICY_SIGNERS_SEARCH_KEYS_CONFIG;
  protected readonly DocumentWriteAction = DocumentWriteAction;
  protected readonly agentEmailAddressesKeySet = agentEmailAddressesKeySet;
  protected readonly AddressModelKeys = AddressModelKeys;
  protected readonly AssociationKeys = AssociationKeys;
  protected readonly ASSOCIATIONS_SEARCH_KEYS_CONFIG = ASSOCIATIONS_SEARCH_KEYS_CONFIG;
  protected readonly isRemovedLog = isRemovedLog;
  protected readonly isLogArray = isLogArray;
  protected readonly ChangeSourceType = ChangeSourceType;
  protected readonly DOCUMENT_WRITE_ACTION_LOOKUP = DOCUMENT_WRITE_ACTION_LOOKUP;

  constructor(@Inject(DOCUMENT) private document: any, protected changesHistoryGridService: ChangesHistoryGridService) {
    this.dataSource = new CustomStore({
      key: 'wasabiPath',
      load: async loadOptions => {
        if (loadOptions.filter) {
          loadOptions.filter = this.addUpdatedBySearchFilter(loadOptions.filter);
        }

        switch (this.changeLogTypeSig()) {
          case ChangeLogsHistoryType.registrant:
            this.conferenceDbId = await firstValueFrom(this.changesHistoryService.conferenceDbId$);
            this.registrantDbId = await firstValueFrom(this.changesHistoryService.registrantDbId$);
            this.wasabiPathFilterValues = [
              [['wasabiPath', 'contains', this.conferenceDbId], 'and', ['wasabiPath', 'contains', this.registrantDbId]],
            ];
            this.collectionFilterValues = [
              ['collection', 'contains', LoggedCollections.registrant],
              'and',
              ['collection', 'contains', LoggedCollections.registrantGuest],
            ];
            this.createUpdatedFieldsList(changeLogsLookup);
            break;

          case ChangeLogsHistoryType.agent:
            this.agentDbId = await firstValueFrom(this.changesHistoryService.agentDbId$);
            this.wasabiPathFilterValues = ['wasabiPath', 'contains', this.agentDbId];
            this.collectionFilterValues = ['collection', 'contains', LoggedCollections.agent];
            this.createUpdatedFieldsList(agentChangeLogsLookup);
            break;
        }
        const response = await this.documentWriteLogElasticSearchService.getFromElastic(loadOptions);
        console.log('response ', response);
        response.data.map(async item => {
          // If the source is missing or marked as "system" (can set as default), try to infer it from the snapshot data.
          if (!item?.changedBySource || item?.changedBySource === ChangeSourceType.system) {
            const wasabiFile = this.wasabiFilePipe.transform(item.wasabiPath);
            const json = await getJsonFromWasabiFile(wasabiFile);

            // Prefer the explicit actionName from the snapshot data
            const actionNameFromSnapshot = json?.newData?.data?.actionName ?? json?.oldData?.data?.actionName;

            if (actionNameFromSnapshot) {
              item.changedBySource = actionNameFromSnapshot;
            } else if (item?.action === ChangeLogsType.removed && json?.oldData?.actionName) {
              item.changedBySource = json.oldData.actionName;
            } else {
              item.changedBySource = ChangeSourceType.system;
            }
          }
        });

        return response;
      },
    });
  }

  ngOnInit(): void {
    this.containerElement = this.changeLogContainer?.nativeElement;
  }

  fullscreen(container: HTMLElement): void {
    this.containerElement = container;
    fullscreen(container, this.document);
  }

  updatedFieldsCalcFilterExpr(filterValue, selectedFilterOperation) {
    let column = this as any;

    if (selectedFilterOperation === 'contains') {
      let filterExpr = [];
      filterValue.forEach(val => {
        filterExpr.length >= 1 ? filterExpr.push('or') : filterExpr.push(['updatedFields', 'contains', val]);
      });
      return filterExpr;
    }
    return column.defaultCalculateFilterExpression.apply(column, arguments);
  }

  protected onRowPrepared(event): void {
    if (event.rowType === 'data') {
      const updatedFields = event.data.updatedFields;
      const hasExcludedFields = updatedFields?.filter(val => !this.excludedChangeLogsFieldsSet.has(val));
      if (!updatedFields?.length || !hasExcludedFields?.length) {
        event.rowElement.style.display = 'none';
      }
    }
  }

  protected onEditorPreparing(event): void {
    if (event.dataField == 'updatedFields' && event.parentType == 'filterRow') {
      event.editorName = 'dxTagBox';
      event.editorOptions = {
        placeholder: 'Select fields...',
        dataSource: this.updatedFieldsList,
        displayExpr: 'description',
        valueExpr: 'value',
        showClearButton: true,
        searchEnabled: true,
        applyValueMode: 'useButtons',
        showSelectionControls: true,
        maxDisplayedTags: 3,
        grouped: true,
        dropDownOptions: { container: this.containerElement },
        onValueChanged: this.onUpdatedFieldsSelectionChanged,
      };
    }
  }

  onSelectionChanged = event => {
    const selectedItem = event.selectedItem.value;
    this.changeLogGridComponent.instance.columnOption('changedBySource', 'filterValue', selectedItem);
  };

  onUpdatedFieldsSelectionChanged = event => {
    const selectedItems = event?.component?.option('selectedItems');
    const selectedKeys = selectedItems?.map(({ value }) => value) ?? [];
    this.changeLogGridComponent.instance.columnOption('updatedFields', 'filterValue', selectedKeys);
  };

  protected onOptionChanged = event => {
    if (event.name === 'searchPanel') {
      this.filterPanelText$.next(event.value);
    }
  };
  /**
   * Adds a diacritics-insensitive search condition for the "updatedBy" field
   * to the existing filter array, based on a search value found in the first group.
   */
  private addUpdatedBySearchFilter = (existingFilter: any): any => {
    if (!existingFilter || !Array.isArray(existingFilter)) {
      return existingFilter;
    }

    const firstFilterGroup = existingFilter[0];
    if (!Array.isArray(firstFilterGroup)) {
      return existingFilter;
    }

    // Recursively find the first "contains" search value in the filter group
    const findSearchValueInFilter = (filterPart: any): string | null => {
      if (!Array.isArray(filterPart)) return null;

      if (filterPart.length === 3 && typeof filterPart[0] === 'string' && filterPart[1] === 'contains') {
        return filterPart[2];
      }

      for (const subPart of filterPart) {
        if (Array.isArray(subPart)) {
          const foundValue = findSearchValueInFilter(subPart);
          if (foundValue) return foundValue;
        }
      }

      return null;
    };

    const searchValue = findSearchValueInFilter(firstFilterGroup);
    if (!searchValue) return existingFilter;

    // Get the list of agents from the signal
    const allAgents = this.agentsLookupSig() ?? [];

    // Normalize the search value for comparison
    const normalizedSearchValue = removeDiacritics(searchValue).toLowerCase();

    // Find all agent IDs whose description matches the search value
    const matchingAgentIds = allAgents
      .filter(agent => {
        const agentDescription = removeDiacritics(agent?.description ?? '').toLowerCase();
        return agentDescription.includes(normalizedSearchValue);
      })
      .map(agent => agent.value);

    if (matchingAgentIds.length === 0) return existingFilter;

    // Build the "updatedBy" filter
    const updatedByFilter =
      matchingAgentIds.length === 1
        ? ['updatedBy', '=', matchingAgentIds[0]]
        : matchingAgentIds.flatMap((id, index) =>
            index > 0 ? ['or', ['updatedBy', '=', id]] : [['updatedBy', '=', id]],
          );

    // Merge the "updatedBy" filter into the first group using OR
    existingFilter[0] = [...firstFilterGroup, 'or', updatedByFilter];

    return existingFilter;
  };

  hasSelectedExcursion = logs => {
    // newly added = Array[null] without data
    return Array.isArray(logs) ? logs.filter(log => log !== null).length : !!logs;
  };

  private createUpdatedFieldsList = lookup => {
    const changeHistoryLookup = lookup.map(item => {
      return {
        category: item.category,
        value: item.value,
        description: item.description ? item.description.split(':').pop().trim() : item.value.trim(),
      };
    });
    this.updatedFieldsList = new DataSource({
      store: new ArrayStore({
        data: changeHistoryLookup ?? [],
        key: 'value',
      }),
      group: 'category',
    });
  };
}
